import {
  Card,
  Flex,
  Link,
  Spinner,
  Switch,
  Text,
  VStack,
} from "@chakra-ui/react";
import { useState } from "react";

import { Restricted } from "@/app/components/Restricted";
import SectionDescription from "@/common/components/SectionDescription";
import { SectionHeading } from "@/common/components/SectionHeading";
import { useErrorToast } from "@/common/hooks/useErrorToast";
import { type OptInFlag, useOptInFlags, useSetOptIn } from "@reflag/react-sdk";

export default function Experiments() {
  const { flags: optInFlags, isLoading } = useOptInFlags();

  return (
    <Flex direction="column" gap={5} maxW="mediumForm">
      <Flex direction="column" gap={1}>
        <SectionHeading>Experiments</SectionHeading>
        <SectionDescription>
          Opt your organization into beta and experimental features. Changes
          apply to everyone in the organization. Want to implement this for your
          own app?{" "}
          <Link
            href="https://docs.reflag.com/guides/self-opt-in"
            target="_blank"
          >
            See the docs
          </Link>{" "}
          for more information.
        </SectionDescription>
      </Flex>

      <Restricted alertStyle="callout" role="admin">
        {isLoading ? (
          <Spinner aria-label="Loading experiments" role="status" />
        ) : optInFlags.length === 0 ? (
          <Card p={4} variant="filled">
            <Text color="dimmed" fontSize="sm">
              No experiments are available right now.
            </Text>
          </Card>
        ) : (
          <VStack align="stretch" spacing={3}>
            {optInFlags.map((flag) => (
              <ExperimentCard key={flag.key} flag={flag} />
            ))}
          </VStack>
        )}
      </Restricted>
    </Flex>
  );
}

function ExperimentCard({ flag }: { flag: OptInFlag }) {
  const setOptIn = useSetOptIn();
  const errorToast = useErrorToast();
  const [isUpdating, setIsUpdating] = useState(false);

  const updateOptIn = async () => {
    setIsUpdating(true);

    try {
      const response = await setOptIn(flag.key, {
        optedIn: !flag.companyOptedIn,
        scope: "company",
      });

      if (response?.ok === false) {
        throw new Error("Opt-in request failed");
      }
    } catch {
      errorToast({
        title: `Failed to update ${flag.name}`,
        description: "Please try again.",
      });
    } finally {
      setIsUpdating(false);
    }
  };

  return (
    <Card
      align="center"
      direction="row"
      gap={4}
      justify="space-between"
      p={4}
      variant="filled"
    >
      <Flex direction="column" gap={1}>
        <Text fontWeight="medium">{flag.name}</Text>
        {flag.description && (
          <Text color="dimmed" fontSize="sm" whiteSpace="pre-wrap">
            {flag.description}
          </Text>
        )}
      </Flex>

      <Flex align="center" flexShrink={0} gap={2}>
        <Spinner
          aria-hidden={!isUpdating}
          aria-label={isUpdating ? `Updating ${flag.name}` : undefined}
          role={isUpdating ? "status" : undefined}
          size="xs"
          visibility={isUpdating ? "visible" : "hidden"}
        />
        <Switch
          aria-busy={isUpdating}
          aria-label={`Enable ${flag.name} for the organization`}
          isChecked={flag.companyOptedIn}
          isDisabled={isUpdating}
          onChange={() => void updateOptIn()}
        />
      </Flex>
    </Card>
  );
}
